import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../components/ui/dialog';
import {
  Users, UserPlus, Mail, RotateCw, XCircle, Shield, ShieldOff,
  ShieldCheck, MoreHorizontal, Loader2, Trash2, AlertTriangle
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

const STATUS_BADGES = {
  active:   { label: 'Active',   cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-400 dark:border-emerald-500/20' },
  disabled: { label: 'Disabled', cls: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/20' },
  pending:  { label: 'Pending',  cls: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-400 dark:border-amber-500/20' },
  accepted: { label: 'Accepted', cls: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-400 dark:border-blue-500/20' },
  revoked:  { label: 'Revoked',  cls: 'bg-muted text-muted-foreground border-border' },
  expired:  { label: 'Expired',  cls: 'bg-muted text-muted-foreground border-border' },
};

const ROLE_LABELS = {
  primary: { label: 'Primary Admin', icon: ShieldCheck, cls: 'text-amber-600 dark:text-amber-400' },
  member:  { label: 'Team Member',   icon: Shield,      cls: 'text-blue-600 dark:text-blue-400' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGES[status] || STATUS_BADGES.active;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cfg.cls}`}
          data-testid={`badge-${status}`}>
      {cfg.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return '\u2014';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(iso) {
  if (!iso) return '\u2014';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/* ── Shared action menu for admin rows ── */
function AdminActionMenu({ admin, actionLoading, onDisable, onEnable, canHardDelete, currentAdminEmail, onHardDelete }) {
  const isSelf = admin.email === currentAdminEmail;
  const isPrimary = admin.role === 'primary';
  // Don't show menu for self (cannot delete own account)
  // Show menu for non-primary admins OR for primary admins when super-admin can hard-delete (but not self)
  const showMenu = !isSelf && (!isPrimary || canHardDelete);
  if (!showMenu) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" data-testid={`admin-actions-${admin.email}`}>
          {actionLoading === admin.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreHorizontal className="w-4 h-4" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {!isPrimary && admin.is_active && (
          <DropdownMenuItem onClick={() => onDisable(admin.id)} className="text-red-600 focus:text-red-600 cursor-pointer" data-testid={`disable-admin-${admin.email}`}>
            <ShieldOff className="w-4 h-4 mr-2" />
            Disable Access
          </DropdownMenuItem>
        )}
        {!isPrimary && !admin.is_active && (
          <DropdownMenuItem onClick={() => onEnable(admin.id)} className="text-emerald-600 focus:text-emerald-600 cursor-pointer" data-testid={`enable-admin-${admin.email}`}>
            <ShieldCheck className="w-4 h-4 mr-2" />
            Re-enable Access
          </DropdownMenuItem>
        )}
        {canHardDelete && !isSelf && (
          <>
            {!isPrimary && <DropdownMenuSeparator />}
            <DropdownMenuItem onClick={() => onHardDelete('admin', admin)} className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-500/10 cursor-pointer" data-testid={`hard-delete-admin-${admin.email}`}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Permanently
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ── Shared action menu for invite rows ── */
function InviteActionMenu({ inv, actionLoading, onResend, onRevoke, canHardDelete, onHardDelete }) {
  const isPendingOrExpired = inv.status === 'pending' || inv.status === 'expired';
  const showMenu = isPendingOrExpired || canHardDelete;
  if (!showMenu) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" data-testid={`invite-actions-${inv.email}`}>
          {actionLoading === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreHorizontal className="w-4 h-4" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {isPendingOrExpired && (
          <DropdownMenuItem onClick={() => onResend(inv.id)} className="cursor-pointer" data-testid={`resend-invite-${inv.email}`}>
            <RotateCw className="w-4 h-4 mr-2" />
            Resend Invite
          </DropdownMenuItem>
        )}
        {inv.status === 'pending' && (
          <DropdownMenuItem onClick={() => onRevoke(inv.id)} className="text-red-600 focus:text-red-600 cursor-pointer" data-testid={`revoke-invite-${inv.email}`}>
            <XCircle className="w-4 h-4 mr-2" />
            Revoke Invite
          </DropdownMenuItem>
        )}
        {canHardDelete && (
          <>
            {isPendingOrExpired && <DropdownMenuSeparator />}
            <DropdownMenuItem onClick={() => onHardDelete('invite', inv)} className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-500/10 cursor-pointer" data-testid={`hard-delete-invite-${inv.email}`}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Permanently
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ── Mobile card label/value row ── */
function DetailRow({ label, children }) {
  return (
    <div className="flex justify-between items-center py-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground text-right">{children}</span>
    </div>
  );
}


export function AdminTeam() {
  const { adminRole } = useOutletContext();
  const isPrimaryAdmin = adminRole === 'primary';
  const [admins, setAdmins] = useState([]);
  const [invites, setInvites] = useState([]);
  const [canHardDelete, setCanHardDelete] = useState(false);
  const [currentAdminEmail, setCurrentAdminEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  // Hard-delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'admin'|'invite', record }
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const fetchTeam = useCallback(async () => {
    try {
      const res = await adminAPI.getTeam();
      setAdmins(res.data.admins || []);
      setInvites(res.data.invites || []);
      setCanHardDelete(res.data.can_hard_delete || false);
      setCurrentAdminEmail(res.data.current_admin_email || '');
    } catch (err) {
      toast.error('Failed to load admin team');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await adminAPI.createTeamInvite({ email: inviteEmail.trim(), name: inviteName.trim() || null });
      toast.success(`Invite sent to ${inviteEmail.trim()}`);
      setInviteOpen(false);
      setInviteEmail('');
      setInviteName('');
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const handleResend = async (inviteId) => {
    setActionLoading(inviteId);
    try { await adminAPI.resendTeamInvite(inviteId); toast.success('Invite resent'); fetchTeam(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to resend'); }
    finally { setActionLoading(null); }
  };

  const handleRevoke = async (inviteId) => {
    setActionLoading(inviteId);
    try { await adminAPI.revokeTeamInvite(inviteId); toast.success('Invite revoked'); fetchTeam(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to revoke'); }
    finally { setActionLoading(null); }
  };

  const handleDisable = async (adminId) => {
    setActionLoading(adminId);
    try { await adminAPI.disableAdmin(adminId); toast.success('Admin disabled'); fetchTeam(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to disable admin'); }
    finally { setActionLoading(null); }
  };

  const handleEnable = async (adminId) => {
    setActionLoading(adminId);
    try { await adminAPI.enableAdmin(adminId); toast.success('Admin re-enabled'); fetchTeam(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to enable admin'); }
    finally { setActionLoading(null); }
  };

  const openDeleteConfirm = (type, record) => {
    setDeleteTarget({ type, record });
    setDeleteConfirmText('');
  };

  const handleHardDelete = async () => {
    if (!deleteTarget || deleteConfirmText !== 'DELETE') return;
    setDeleting(true);
    try {
      if (deleteTarget.type === 'admin') {
        await adminAPI.hardDeleteAdmin(deleteTarget.record.id);
      } else {
        await adminAPI.hardDeleteInvite(deleteTarget.record.id);
      }
      toast.success(`${deleteTarget.record.email} permanently deleted`);
      setDeleteTarget(null);
      setDeleteConfirmText('');
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="admin-team-loading">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-full overflow-x-hidden" data-testid="admin-team-page">
      <div className="px-3 sm:px-6 lg:px-8 py-5 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl font-bold text-foreground flex items-center gap-2.5">
              <Users className="w-5 h-5 text-blue-600" />
              Admin Team
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Manage who has access to the admin portal
            </p>
          </div>
          {isPrimaryAdmin && <Button
            onClick={() => setInviteOpen(true)}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2 w-full sm:w-auto"
            data-testid="invite-admin-btn"
          >
            <UserPlus className="w-4 h-4" />
            Invite Admin
          </Button>}
        </div>

        {/* ════════════════════════════════════════════════════════════════
            TEAM MEMBERS
        ════════════════════════════════════════════════════════════════ */}
        <Card className="mb-6">
          <div className="px-4 sm:px-5 py-3.5 border-b">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Team Members ({admins.length})
            </h2>
          </div>

          {/* ── Desktop table (md+) ── */}
          <CardContent className="p-0 hidden md:block">
            <table className="w-full" data-testid="admin-members-table">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">Admin</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">Role</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">Invited By</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3 hidden lg:table-cell">Last Login</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3 hidden lg:table-cell">Created</th>
                  <th className="w-12 px-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {admins.map((admin) => {
                  const roleCfg = ROLE_LABELS[admin.role] || ROLE_LABELS.member;
                  const RoleIcon = roleCfg.icon;
                  return (
                    <tr key={admin.id} className="hover:bg-muted/50 transition-colors" data-testid={`admin-row-${admin.email}`}>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-semibold text-muted-foreground shrink-0">
                            {(admin.name || admin.email)[0].toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{admin.name || '\u2014'}</p>
                            <p className="text-xs text-muted-foreground truncate">{admin.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${roleCfg.cls}`}>
                          <RoleIcon className="w-3.5 h-3.5" />
                          {roleCfg.label}
                        </span>
                      </td>
                      <td className="px-5 py-3.5"><StatusBadge status={admin.is_active ? 'active' : 'disabled'} /></td>
                      <td className="px-5 py-3.5 text-sm text-muted-foreground">{admin.invited_by_email || '\u2014'}</td>
                      <td className="px-5 py-3.5 hidden lg:table-cell text-sm text-muted-foreground">{formatDateTime(admin.last_login_at)}</td>
                      <td className="px-5 py-3.5 hidden lg:table-cell text-sm text-muted-foreground">{formatDate(admin.created_at)}</td>
                      <td className="px-3 py-3.5 text-right">
                        {isPrimaryAdmin && <AdminActionMenu admin={admin} actionLoading={actionLoading} onDisable={handleDisable} onEnable={handleEnable} canHardDelete={canHardDelete} currentAdminEmail={currentAdminEmail} onHardDelete={openDeleteConfirm} />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>

          {/* ── Mobile card list (<md) ── */}
          <div className="md:hidden" data-testid="admin-members-mobile">
            {admins.map((admin, idx) => {
              const roleCfg = ROLE_LABELS[admin.role] || ROLE_LABELS.member;
              const RoleIcon = roleCfg.icon;
              return (
                <div key={admin.id} className={`px-4 py-5 ${idx > 0 ? 'border-t' : ''}`} data-testid={`admin-row-${admin.email}`}>
                  {/* Top row: avatar + name + action */}
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-sm font-semibold text-muted-foreground shrink-0">
                        {(admin.name || admin.email)[0].toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">{admin.name || '\u2014'}</p>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{admin.email}</p>
                      </div>
                    </div>
                    <div className="shrink-0 pt-0.5">
                      {isPrimaryAdmin && <AdminActionMenu admin={admin} actionLoading={actionLoading} onDisable={handleDisable} onEnable={handleEnable} canHardDelete={canHardDelete} currentAdminEmail={currentAdminEmail} onHardDelete={openDeleteConfirm} />}
                    </div>
                  </div>
                  {/* Detail rows */}
                  <div className="pl-12">
                    <DetailRow label="Role">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium ${roleCfg.cls}`}>
                        <RoleIcon className="w-3 h-3" />
                        {roleCfg.label}
                      </span>
                    </DetailRow>
                    <DetailRow label="Status">
                      <StatusBadge status={admin.is_active ? 'active' : 'disabled'} />
                    </DetailRow>
                    {admin.invited_by_email && (
                      <DetailRow label="Invited by">{admin.invited_by_email}</DetailRow>
                    )}
                    <DetailRow label="Last login">{formatDateTime(admin.last_login_at)}</DetailRow>
                    <DetailRow label="Created">{formatDate(admin.created_at)}</DetailRow>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* ════════════════════════════════════════════════════════════════
            INVITATIONS
        ════════════════════════════════════════════════════════════════ */}
        {invites.length > 0 && (
          <Card>
            <div className="px-4 sm:px-5 py-3.5 border-b">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Invitations ({invites.length})
              </h2>
            </div>

            {/* ── Desktop table (md+) ── */}
            <CardContent className="p-0 hidden md:block">
              <table className="w-full" data-testid="admin-invites-table">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">Recipient</th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">Status</th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">Invited By</th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">Sent</th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3 hidden lg:table-cell">Expires</th>
                    <th className="w-12 px-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {invites.map((inv) => (
                    <tr key={inv.id} className="hover:bg-muted/50 transition-colors" data-testid={`invite-row-${inv.email}`}>
                      <td className="px-5 py-3.5">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{inv.name || inv.email}</p>
                          {inv.name && <p className="text-xs text-muted-foreground truncate">{inv.email}</p>}
                        </div>
                      </td>
                      <td className="px-5 py-3.5"><StatusBadge status={inv.status} /></td>
                      <td className="px-5 py-3.5 text-sm text-muted-foreground">{inv.invited_by_email || '\u2014'}</td>
                      <td className="px-5 py-3.5 text-sm text-muted-foreground">{formatDate(inv.created_at)}</td>
                      <td className="px-5 py-3.5 hidden lg:table-cell text-sm text-muted-foreground">{formatDate(inv.expires_at)}</td>
                      <td className="px-3 py-3.5 text-right">
                        {isPrimaryAdmin && <InviteActionMenu inv={inv} actionLoading={actionLoading} onResend={handleResend} onRevoke={handleRevoke} canHardDelete={canHardDelete} onHardDelete={openDeleteConfirm} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>

            {/* ── Mobile card list (<md) ── */}
            <div className="md:hidden" data-testid="admin-invites-mobile">
              {invites.map((inv, idx) => (
                <div key={inv.id} className={`px-4 py-5 ${idx > 0 ? 'border-t' : ''}`} data-testid={`invite-row-${inv.email}`}>
                  {/* Top row: name/email + status + action */}
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground truncate">{inv.name || inv.email}</p>
                      {inv.name && <p className="text-xs text-muted-foreground truncate mt-0.5">{inv.email}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 pt-0.5">
                      <StatusBadge status={inv.status} />
                      {isPrimaryAdmin && <InviteActionMenu inv={inv} actionLoading={actionLoading} onResend={handleResend} onRevoke={handleRevoke} />}
                    </div>
                  </div>
                  {/* Detail rows */}
                  <div>
                    <DetailRow label="Invited by">{inv.invited_by_email || '\u2014'}</DetailRow>
                    <DetailRow label="Sent">{formatDate(inv.created_at)}</DetailRow>
                    <DetailRow label="Expires">{formatDate(inv.expires_at)}</DetailRow>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Invite Modal */}
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogContent className="sm:max-w-md" data-testid="invite-admin-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                Invite Admin
              </DialogTitle>
              <DialogDescription>
                Send an email invitation to join the admin portal.
                The invite will expire in 7 days.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleInvite} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Email Address *</Label>
                <Input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="admin@example.com"
                  data-testid="invite-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Name (optional)</Label>
                <Input
                  type="text"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="John Doe"
                  data-testid="invite-name-input"
                />
              </div>
              <DialogFooter className="gap-2 pt-2 flex-col sm:flex-row">
                <Button type="button" variant="outline" onClick={() => setInviteOpen(false)} className="w-full sm:w-auto">
                  Cancel
                </Button>
                <Button type="submit" disabled={inviting} className="bg-blue-600 hover:bg-blue-700 text-white gap-2 w-full sm:w-auto" data-testid="send-invite-btn">
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  Send Invite
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Hard Delete Confirmation Dialog */}
        <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteConfirmText(''); } }}>
          <DialogContent className="sm:max-w-md" data-testid="hard-delete-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Delete Permanently
              </DialogTitle>
              <DialogDescription className="text-sm pt-2">
                This will <strong className="text-red-600">permanently delete</strong> the {deleteTarget?.type === 'admin' ? 'admin account' : 'invite record'} for{' '}
                <strong className="text-foreground">{deleteTarget?.record?.email}</strong>.
                <br /><br />
                This action <strong>cannot be undone</strong>. The record will be removed from the database entirely.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <Label className="text-sm font-medium">
                Type <strong className="text-red-600 font-mono">DELETE</strong> to confirm
              </Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="font-mono"
                autoComplete="off"
                data-testid="hard-delete-confirm-input"
              />
            </div>
            <DialogFooter className="gap-2 pt-3 flex-col sm:flex-row">
              <Button type="button" variant="outline" onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }} className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button
                onClick={handleHardDelete}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
                className="bg-red-600 hover:bg-red-700 text-white gap-2 w-full sm:w-auto disabled:opacity-50"
                data-testid="hard-delete-confirm-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete Permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default AdminTeam;
